import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FEE_SCHEDULE_DOC_TYPES,
  FeeScheduleSchema,
  expectedScheduleKind,
  type AreaTier,
  type FeeSchedule,
  type FeeScheduleDocType,
  type GuaranteeSchedule,
  type WorkRateItem,
  type WorkRateSchedule,
} from "@juro/shared";
import { feeSchedulesApi, getDocTypeLabel } from "../api.js";
import { toast } from "../components/Toast.js";
import { Button } from "../components/ui/Button.js";
import { useScreenView } from "../lib/analytics.js";

/**
 * 料金表（tenant_fee_schedules）の単価編集画面。
 *
 * 設計: docs/design/fee-schedules.md（§6 の「料金表の設定画面」残タスク）
 *       docs/design/work-item-picker-ux.md §7 Phase 0-②
 *
 * - 料金表は土地計算書・建物計算書・保証協会請求書の唯一の価格決定源
 * - 保存は明示ボタンのみ（オートセーブしない）。売上に直結するため誤爆を避ける
 * - 保存前に FeeScheduleSchema で検証（サーバー側でも Zod 検証される）
 * - 変更は「今後作成・再計算する書類」にのみ効く。発行済み書類は
 *   documents.calc_result_json のスナップショットで不変
 */

// ── 共通スタイル ────────────────────────────────────────────────────────────

const inputStyle: CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "5px 8px",
  fontSize: 13,
  fontFamily: "inherit",
  color: "#0f172a",
  background: "#fff",
  minWidth: 0,
};

const numInputStyle: CSSProperties = { ...inputStyle, textAlign: "right", width: 110 };

const fieldLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#64748b",
  marginBottom: 4,
};

const cardStyle: CSSProperties = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 20,
};

const thStyle: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
  textAlign: "left",
  padding: "4px 6px",
  whiteSpace: "nowrap",
};

const tdStyle: CSSProperties = { padding: "4px 6px", verticalAlign: "middle" };

function tabStyle(active: boolean): CSSProperties {
  return {
    border: `1px solid ${active ? "#0f766e" : "#cbd5e1"}`,
    background: active ? "#f0fdfa" : "#fff",
    color: active ? "#0f766e" : "#475569",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    fontFamily: "inherit",
    cursor: "pointer",
  };
}

function badgeStyle(custom: boolean): CSSProperties {
  return {
    border: `1px solid ${custom ? "#fed7aa" : "#ccfbf1"}`,
    background: custom ? "#fff7ed" : "#f0fdfa",
    color: custom ? "#c2410c" : "#0f766e",
    borderRadius: 999,
    padding: "2px 9px",
    fontSize: 11,
    fontWeight: 800,
  };
}

// ── 数値入力（整数円 / 率）────────────────────────────────────────────────
// 親の値と同期しつつ、入力途中の文字列（"" や "0."）を壊さない。

function NumberField({
  value,
  onChange,
  integer = true,
  step,
  style,
  title,
}: {
  value: number;
  onChange: (n: number) => void;
  integer?: boolean;
  step?: string;
  style?: CSSProperties;
  title?: string;
}) {
  const parse = (raw: string): number => {
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return 0;
    return integer ? Math.floor(n) : n;
  };
  const [text, setText] = useState(String(value));
  // 親の値が外から変わった時だけ入力文字列を差し替える（"" や "0." を潰さない）
  useEffect(() => {
    if (parse(text) !== value) setText(String(value));
  }, [value]); // text は意図的に依存に入れない

  return (
    <input
      type="number"
      min={0}
      step={step ?? (integer ? "1" : "any")}
      title={title}
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        // 負の値は受け付けない（この画面の数値は金額・率・段階の下限のいずれも 0 以上）。
        // 「-」を入力しても parse() は 0 に潰すだけで value が変わらないと再同期されず
        // 表示だけ "-" が残ってしまうため、先頭が "-" の入力はここで即座に据え置いて弾く。
        if (raw.trimStart().startsWith("-")) {
          setText(String(value));
          return;
        }
        setText(raw);
        onChange(parse(raw));
      }}
      style={{ ...numInputStyle, ...style }}
    />
  );
}

// ── ページ本体 ──────────────────────────────────────────────────────────────

export function FeeSchedulePage() {
  useScreenView("fee_schedules");
  const [active, setActive] = useState<FeeScheduleDocType>("land_calculation");
  // タブ切替・離脱時に未保存の変更を誤って破棄しないためのガード（必須B）
  const [dirty, setDirty] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["fee-schedules"],
    queryFn: () => feeSchedulesApi.list(),
  });

  const entry = data?.data?.[active];

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  function handleTabClick(docType: FeeScheduleDocType) {
    if (docType === active) return;
    if (dirty && !window.confirm("未保存の変更があります。破棄して切り替えますか？")) return;
    setActive(docType);
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 6 }}>
        <Link to="/settings" style={{ color: "#0f766e", fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
          ← 設定
        </Link>
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 6px" }}>料金表</h1>
      <p style={{ fontSize: 13, color: "#64748b", margin: "0 0 16px" }}>
        土地計算書・建物計算書・保証協会請求書の単価を編集します。金額はこの料金表から自動計算されます。
      </p>

      <div
        style={{
          background: "#fff7ed",
          border: "1px solid #fed7aa",
          color: "#c2410c",
          borderRadius: 8,
          padding: "10px 14px",
          fontSize: 12.5,
          fontWeight: 600,
          lineHeight: 1.6,
          marginBottom: 16,
          maxWidth: 900,
        }}
      >
        <strong>発行済みの書類の金額は変わりません</strong>（発行時点の計算結果をスナップショットとして保存しているため）。
        <br />
        一方で<strong>下書きの計算書は開いた時点で新しい単価により再計算されます</strong>。作成途中の下書きがある場合は、
        金額が変わっていないか確認してください。
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {FEE_SCHEDULE_DOC_TYPES.map((docType) => (
          <button key={docType} style={tabStyle(active === docType)} onClick={() => handleTabClick(docType)}>
            {getDocTypeLabel(docType)}
            {data?.data?.[docType]?.source === "custom" && (
              <span style={{ marginLeft: 6, fontSize: 11, color: "#c2410c" }}>●</span>
            )}
          </button>
        ))}
      </div>

      {isLoading && <p style={{ color: "#94a3b8", fontSize: 13 }}>読み込み中…</p>}
      {error && <p style={{ color: "#dc2626", fontSize: 13 }}>{(error as Error).message}</p>}

      {entry && (
        <ScheduleEditor
          key={`${active}:${entry.source}`}
          docType={active}
          initial={entry.schedule}
          source={entry.source}
          onDirtyChange={setDirty}
        />
      )}
    </div>
  );
}

// ── 書類ごとのエディタ（保存 / 既定に戻す）────────────────────────────────

function ScheduleEditor({
  docType,
  initial,
  source,
  onDirtyChange,
}: {
  docType: FeeScheduleDocType;
  initial: FeeSchedule;
  source: "custom" | "default";
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<FeeSchedule>(() => clone(initial));

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initial),
    [draft, initial],
  );

  // 未保存の変更を親（タブ切替・離脱ガード）へ伝える（必須B）
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const saveMut = useMutation({
    mutationFn: (schedule: FeeSchedule) => feeSchedulesApi.update(docType, schedule),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fee-schedules"] });
      toast(`${getDocTypeLabel(docType)}の料金表を保存しました`);
    },
    onError: (e) => toast(e.message, "error"),
  });

  const resetMut = useMutation({
    mutationFn: () => feeSchedulesApi.reset(docType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["fee-schedules"] });
      toast(`${getDocTypeLabel(docType)}の料金表を既定に戻しました`);
    },
    onError: (e) => toast(e.message, "error"),
  });

  function handleSave() {
    const normalized = normalizeSchedule(draft);
    if (normalized.kind !== expectedScheduleKind(docType)) {
      toast("書類種別と料金表の種類が一致しません", "error");
      return;
    }
    if (normalized.kind === "work_rate") {
      const areaTableError = validateAreaTables(getDocTypeLabel(docType), normalized.area_tables);
      if (areaTableError) {
        toast(areaTableError, "error");
        return;
      }
    }
    const parsed = FeeScheduleSchema.safeParse(normalized);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      const where = issue?.path.join(".") ?? "";
      toast(`入力に誤りがあります${where ? `（${where}）` : ""}: ${issue?.message ?? "検証に失敗しました"}`, "error");
      return;
    }
    setDraft(parsed.data);
    saveMut.mutate(parsed.data);
  }

  function handleReset() {
    if (!window.confirm(`${getDocTypeLabel(docType)}の料金表を既定値に戻します。カスタマイズした単価は失われます。よろしいですか？`)) return;
    resetMut.mutate();
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          padding: "10px 14px",
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          marginBottom: 14,
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{getDocTypeLabel(docType)}</span>
        <span style={badgeStyle(source === "custom")}>
          {source === "custom" ? "カスタム料金表" : "既定料金表"}
        </span>
        {isDirty && (
          <span style={{ fontSize: 12, fontWeight: 800, color: "#c2410c" }}>未保存の変更があります</span>
        )}
        <div style={{ flex: 1 }} />
        {isDirty && (
          <Button variant="ghost" onClick={() => setDraft(clone(initial))} disabled={saveMut.isPending}>
            変更を破棄
          </Button>
        )}
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={source !== "custom" || resetMut.isPending || saveMut.isPending}
          title={source === "custom" ? "カスタマイズを破棄して既定単価に戻します" : "既定料金表のままです"}
        >
          {resetMut.isPending ? "戻しています…" : "既定に戻す"}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!isDirty || saveMut.isPending}>
          {saveMut.isPending ? "保存中…" : "保存"}
        </Button>
      </div>

      {draft.kind === "work_rate" ? (
        <WorkRateEditor schedule={draft} onChange={setDraft} />
      ) : (
        <GuaranteeEditor schedule={draft} onChange={setDraft} />
      )}
    </div>
  );
}

// ── 土地計算書 / 建物計算書（work_rate）────────────────────────────────────

function WorkRateEditor({
  schedule,
  onChange,
}: {
  schedule: WorkRateSchedule;
  onChange: (s: WorkRateSchedule) => void;
}) {
  const patch = (p: Partial<WorkRateSchedule>) => onChange({ ...schedule, ...p });

  const patchItem = (index: number, p: Partial<WorkRateItem>) => {
    const items = schedule.items.map((item, i) => (i === index ? { ...item, ...p } : item));
    patch({ items });
  };

  const sectionKeys = schedule.sections.map((s) => s.key);
  const orphanItems = schedule.items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !sectionKeys.includes(item.section));

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {schedule.sections.map((section) => {
        const rows = schedule.items
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => item.section === section.key);
        if (rows.length === 0) return null;
        return (
          <ItemSection
            key={section.key}
            title={section.label}
            rows={rows}
            areaTableKeys={Object.keys(schedule.area_tables)}
            onPatchItem={patchItem}
          />
        );
      })}

      {orphanItems.length > 0 && (
        <ItemSection
          title="（未分類）"
          rows={orphanItems}
          areaTableKeys={Object.keys(schedule.area_tables)}
          onPatchItem={patchItem}
        />
      )}

      <AreaTablesEditor
        tables={schedule.area_tables}
        onChange={(area_tables) => patch({ area_tables })}
      />

      <section style={cardStyle}>
        <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>旅費</h2>
        <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 14px" }}>
          旅費 = （入力 km − 控除 km。マイナスは 0）× 単価
        </p>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={fieldLabelStyle}>単価（円/km）</label>
            <NumberField value={schedule.travel_unit_price} onChange={(travel_unit_price) => patch({ travel_unit_price })} />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={fieldLabelStyle}>控除距離（km）</label>
            <NumberField value={schedule.travel_deduct_km} onChange={(travel_deduct_km) => patch({ travel_deduct_km })} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 240 }}>
            <label style={fieldLabelStyle}>摘要（帳票の注記）</label>
            <input
              style={inputStyle}
              value={schedule.travel_note ?? ""}
              onChange={(e) => patch({ travel_note: e.target.value })}
              placeholder="（往復−２０ｋｍ）×回＝ｋｍ"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function ItemSection({
  title,
  rows,
  areaTableKeys,
  onPatchItem,
}: {
  title: string;
  rows: { item: WorkRateItem; index: number }[];
  areaTableKeys: string[];
  onPatchItem: (index: number, patch: Partial<WorkRateItem>) => void;
}) {
  return (
    <section style={cardStyle}>
      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 12px" }}>
        {title}
        <span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginLeft: 8 }}>{rows.length}作業</span>
      </h2>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: "34%" }}>作業名</th>
            <th style={{ ...thStyle, width: 70 }}>単位</th>
            <th style={{ ...thStyle, width: 130 }}>単価（円）</th>
            <th style={{ ...thStyle, width: 130 }}>加算単価（円）</th>
            <th style={thStyle}>摘要</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ item, index }) => (
            <tr key={item.key} style={{ borderTop: "1px solid #f1f5f9" }}>
              <td style={tdStyle}>
                <input
                  style={{ ...inputStyle, width: "100%" }}
                  value={item.label}
                  onChange={(e) => onPatchItem(index, { label: e.target.value })}
                />
                <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 2 }}>{item.key}</div>
              </td>
              <td style={tdStyle}>
                <input
                  style={{ ...inputStyle, width: 60 }}
                  value={item.unit}
                  onChange={(e) => onPatchItem(index, { unit: e.target.value })}
                />
              </td>
              <td style={tdStyle}>
                {item.pricing.type === "fixed" ? (
                  <NumberField
                    value={item.pricing.unit_price}
                    onChange={(unit_price) => onPatchItem(index, { pricing: { type: "fixed", unit_price } })}
                  />
                ) : (
                  <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 700 }}>
                    面積段階表
                    <span style={{ color: "#94a3b8", fontWeight: 600 }}>
                      {areaTableKeys.includes(item.pricing.table) ? `（${item.pricing.table}）` : `（${item.pricing.table}・未定義）`}
                    </span>
                  </span>
                )}
              </td>
              <td style={tdStyle}>
                {item.factor_mode === "surcharge" ? (
                  <NumberField
                    value={item.surcharge_unit_price ?? 0}
                    onChange={(surcharge_unit_price) => onPatchItem(index, { surcharge_unit_price })}
                    title="加減率を入力した時に「数量×単価 ＋ 加算単価×率」で加算される単価"
                  />
                ) : (
                  <span style={{ fontSize: 11.5, color: "#cbd5e1" }}>—</span>
                )}
              </td>
              <td style={tdStyle}>
                <input
                  style={{ ...inputStyle, width: "100%" }}
                  value={item.note ?? ""}
                  onChange={(e) => onPatchItem(index, { note: e.target.value })}
                  placeholder="（帳票の摘要欄に出る既定文言）"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function AreaTablesEditor({
  tables,
  onChange,
}: {
  tables: Record<string, AreaTier[]>;
  onChange: (tables: Record<string, AreaTier[]>) => void;
}) {
  const keys = Object.keys(tables);
  if (keys.length === 0) return null;

  const setTiers = (key: string, tiers: AreaTier[]) => onChange({ ...tables, [key]: tiers });

  return (
    <section style={cardStyle}>
      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>面積段階表</h2>
      <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px" }}>
        面積が「下限（超）」を超え、次の段階の下限以下のときにその標準報酬額を適用します。
        保存時に下限の昇順へ並べ替えます。
      </p>
      {keys.map((key) => {
        const tiers = tables[key] ?? [];
        return (
          <details key={key} style={{ border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 10 }}>
            <summary style={{ cursor: "pointer", padding: "8px 12px", fontSize: 13, fontWeight: 800, color: "#334155" }}>
              {key}
              <span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", marginLeft: 8 }}>{tiers.length}段階</span>
            </summary>
            <div style={{ padding: "0 12px 12px" }}>
              <table style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 140 }}>下限（㎡・超）</th>
                    <th style={{ ...thStyle, width: 140 }}>標準報酬額（円）</th>
                    <th style={thStyle} />
                  </tr>
                </thead>
                <tbody>
                  {tiers.map((tier, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #f1f5f9" }}>
                      <td style={tdStyle}>
                        <NumberField
                          integer={false}
                          value={tier.over}
                          onChange={(over) => setTiers(key, tiers.map((t, j) => (j === i ? { ...t, over } : t)))}
                        />
                      </td>
                      <td style={tdStyle}>
                        <NumberField
                          value={tier.amount}
                          onChange={(amount) => setTiers(key, tiers.map((t, j) => (j === i ? { ...t, amount } : t)))}
                        />
                      </td>
                      <td style={tdStyle}>
                        <Button
                          variant="ghost"
                          style={{ color: "#dc2626" }}
                          disabled={tiers.length <= 1}
                          onClick={() => setTiers(key, tiers.filter((_, j) => j !== i))}
                        >
                          削除
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 8 }}>
                <Button
                  variant="outline"
                  onClick={() => {
                    const last = tiers[tiers.length - 1];
                    setTiers(key, [...tiers, { over: last ? last.over + 1000 : 0, amount: last ? last.amount : 0 }]);
                  }}
                >
                  段階を追加
                </Button>
              </div>
            </div>
          </details>
        );
      })}
    </section>
  );
}

// ── 保証協会請求書（guarantee）────────────────────────────────────────────

const GUARANTEE_FIELDS: {
  key: keyof Omit<GuaranteeSchedule, "kind" | "registration_tax_rate">;
  label: string;
  desc: string;
}[] = [
  { key: "base_fee",                   label: "基本報酬等（円）",       desc: "移転1件あたりの基本報酬" },
  { key: "inspection_unit_price",      label: "閲覧 単価（円）",         desc: "報酬側に計上" },
  { key: "transcript_unit_price",      label: "謄本 単価（円・報酬側）", desc: "報酬側に計上" },
  { key: "count_surcharge_unit_price", label: "個数加算 単価（円・税側）", desc: "印紙（閲覧）側に計上" },
  { key: "transcript_tax_unit_price",  label: "謄本 単価（円・税側）",   desc: "印紙（謄本）側に計上" },
];

function GuaranteeEditor({
  schedule,
  onChange,
}: {
  schedule: GuaranteeSchedule;
  onChange: (s: GuaranteeSchedule) => void;
}) {
  return (
    <section style={{ ...cardStyle, maxWidth: 560 }}>
      <h2 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 4px" }}>単価・税率</h2>
      <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 16px" }}>
        1債務者ごとに「基本報酬等 ＋ 閲覧 ＋ 謄本」を報酬計、「登録免許税 ＋ 個数加算 ＋ 謄本（税側）」を税計として計算します。
      </p>

      <div style={{ display: "grid", gap: 12 }}>
        {GUARANTEE_FIELDS.map((f) => (
          <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{f.label}</div>
              <div style={{ fontSize: 11.5, color: "#64748b" }}>{f.desc}</div>
            </div>
            <NumberField value={schedule[f.key]} onChange={(v) => onChange({ ...schedule, [f.key]: v })} />
          </div>
        ))}

        <div style={{ display: "flex", alignItems: "center", gap: 12, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>登録免許税率（%）</div>
            <div style={{ fontSize: 11.5, color: "#64748b" }}>
              課税価格を1,000円未満切捨 → ×税率 → 100円未満切捨（現在 {formatRate(schedule.registration_tax_rate)}）。
              例: 0.4% の場合は「0.4」と入力してください
            </div>
          </div>
          <NumberField
            integer={false}
            step="0.001"
            // 保存値（registration_tax_rate）は割合の生値（0.002 等）。入力は誤操作防止のため % 単位にする。
            value={rateToPercentInput(schedule.registration_tax_rate)}
            onChange={(percent) => onChange({ ...schedule, registration_tax_rate: percentInputToRate(percent) })}
          />
        </div>
      </div>
    </section>
  );
}

/** 保存値（割合）→ 入力欄表示（%）。浮動小数の誤差を丸めて吸収する */
function rateToPercentInput(rate: number): number {
  return Math.round(rate * 100 * 1e6) / 1e6;
}

/** 入力欄（%）→ 保存値（割合）。浮動小数の誤差を丸めて吸収する */
function percentInputToRate(percent: number): number {
  return Math.round((percent / 100) * 1e6) / 1e6;
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(3).replace(/\.?0+$/, "")}%`;
}

/**
 * 面積段階表の保存前バリデーション（必須C）。
 * lookupAreaTier（packages/shared/src/fee-schedule.ts）は
 *   - 先頭段階の下限（over）未満の面積に対して 0 を返す（先頭が over:0 でないと無警告で 0 円になる）
 *   - 同じ over が複数あると「昇順ソート後、後に並んだ方が勝つ」という未定義動作になる
 * ため、どちらも保存を止めて書類名・テーブル名つきで警告する。自動マージはしない。
 */
function validateAreaTables(docLabel: string, areaTables: Record<string, AreaTier[]>): string | null {
  for (const [key, tiers] of Object.entries(areaTables)) {
    if (tiers.length === 0) continue;
    if (tiers[0]?.over !== 0) {
      return `${docLabel}の面積段階表「${key}」は先頭の下限（超）が 0 である必要があります（0㎡からの金額が 0 円になってしまいます）`;
    }
    const seen = new Set<number>();
    for (const tier of tiers) {
      if (seen.has(tier.over)) {
        return `${docLabel}の面積段階表「${key}」に下限（超）が重複する段階があります（${tier.over}）。値を変えてから保存してください`;
      }
      seen.add(tier.over);
    }
  }
  return null;
}

// ── ユーティリティ ──────────────────────────────────────────────────────────

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** 保存前の正規化: 文字列トリム・整数化・面積段階の昇順ソート */
function normalizeSchedule(schedule: FeeSchedule): FeeSchedule {
  if (schedule.kind === "guarantee") {
    return {
      ...schedule,
      base_fee:                   Math.max(0, Math.floor(schedule.base_fee)),
      inspection_unit_price:      Math.max(0, Math.floor(schedule.inspection_unit_price)),
      transcript_unit_price:      Math.max(0, Math.floor(schedule.transcript_unit_price)),
      count_surcharge_unit_price: Math.max(0, Math.floor(schedule.count_surcharge_unit_price)),
      transcript_tax_unit_price:  Math.max(0, Math.floor(schedule.transcript_tax_unit_price)),
      registration_tax_rate:      Math.max(0, schedule.registration_tax_rate),
    };
  }

  const area_tables: Record<string, AreaTier[]> = {};
  for (const [key, tiers] of Object.entries(schedule.area_tables)) {
    area_tables[key] = [...tiers]
      .map((t) => ({ over: Math.max(0, t.over), amount: Math.max(0, Math.floor(t.amount)) }))
      .sort((a, b) => a.over - b.over);
  }

  return {
    ...schedule,
    items: schedule.items.map((item) => ({
      ...item,
      label: item.label.trim(),
      unit:  item.unit.trim(),
      note:  item.note?.trim() ? item.note.trim() : undefined,
      pricing: item.pricing.type === "fixed"
        ? { type: "fixed" as const, unit_price: Math.max(0, Math.floor(item.pricing.unit_price)) }
        : item.pricing,
      surcharge_unit_price: item.surcharge_unit_price === undefined
        ? undefined
        : Math.max(0, Math.floor(item.surcharge_unit_price)),
    })),
    area_tables,
    travel_unit_price: Math.max(0, Math.floor(schedule.travel_unit_price)),
    travel_deduct_km:  Math.max(0, Math.floor(schedule.travel_deduct_km)),
    travel_note: schedule.travel_note?.trim() ? schedule.travel_note.trim() : undefined,
  };
}
